import useUiTranslation from "../utils/useUiTranslation";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from '../api/axios';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  CartesianGrid,
} from 'recharts';
import usePageTranslation from '../utils/usePageTranslation';
import Card from '../components/Card';
import { getPublicPriorityQueue } from '../api/procurementPriority';
import { CurrentProcurementPriorities } from '../components/priority/ProcurementPriorityViews';
import { LayoutDashboard, RefreshCw } from 'lucide-react';
import AnalyticsPageHeader from '../components/analytics/AnalyticsPageHeader';

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff4d4f', '#00C49F'];
const DASHBOARD_REFRESH_EVENT = 'dashboard:refresh';

const Dashboard = () => {
  const uiText = useUiTranslation();
  const translate = usePageTranslation('dashboard');
  const [summary, setSummary] = useState(null);
  const [departmentSpending, setDepartmentSpending] = useState([]);
  const [departmentRequestCosts, setDepartmentRequestCosts] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [error, setError] = useState('');
  const [priorityState, setPriorityState] = useState({ status: 'loading', entries: [] });

  useEffect(() => {
    let active = true;
    const refresh = () => {
      if (document.visibilityState === 'hidden') return;
      getPublicPriorityQueue({ limit: 5 }).then(result => active && setPriorityState({ status: 'ready', entries: (result.data || []).slice(0, 5) })).catch(err => active && setPriorityState({ status: err.response?.status === 404 || err.response?.status === 503 ? 'unavailable' : 'error', entries: [] }));
    };
    refresh();
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => { active = false; window.clearInterval(timer); window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, []);

  const formatAmount = (value) =>
    Number(value || 0).toLocaleString(undefined, {
      maximumFractionDigits: 0,
    });

  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get('/dashboard/summary');
      setSummary(res.data);
      setError('');
    } catch (err) {
      console.error('❌ Failed to fetch dashboard data:', err);
      setError(
        translate('failedToLoad', { defaultValue: 'Failed to load dashboard' })
      );
    }
  }, [translate]);

  const fetchSpending = useCallback(async (selectedYear) => {
    try {
      const res = await axios.get('/dashboard/department-spending', {
        params: { year: selectedYear },
      });
      setDepartmentSpending(res.data);
    } catch (err) {
      console.error('❌ Failed to fetch department spending:', err);
    }
  }, []);

  const fetchDepartmentRequestCosts = useCallback(async (selectedYear) => {
    try {
      const res = await axios.get('/dashboard/department-request-costs', {
        params: { year: selectedYear },
      });
      setDepartmentRequestCosts(res.data);
    } catch (err) {
      console.error('❌ Failed to fetch department request costs:', err);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchSpending(year);
    fetchDepartmentRequestCosts(year);
  }, [fetchSpending, fetchDepartmentRequestCosts, year]);

  useEffect(() => {
    const handleDashboardRefresh = () => {
      fetchSummary();
      fetchSpending(year);
      fetchDepartmentRequestCosts(year);
    };

    window.addEventListener(DASHBOARD_REFRESH_EVENT, handleDashboardRefresh);
    return () =>
      window.removeEventListener(DASHBOARD_REFRESH_EVENT, handleDashboardRefresh);
  }, [fetchSummary, fetchSpending, fetchDepartmentRequestCosts, year]);

  const { deptChartData, deptNames } = useMemo(() => {
    if (!departmentSpending.length) return { deptChartData: [], deptNames: [] };
    const months = Array.from(
      new Set(departmentSpending.map((d) => d.month))
    ).sort();
    const departments = Array.from(
      new Set(departmentSpending.map((d) => d.department))
    );
    const data = months.map((m) => {
      const entry = { month: m };
      departmentSpending
        .filter((d) => d.month === m)
        .forEach((d) => {
          entry[d.department] = Number(d.total_cost);
        });
      return entry;
    });
    return { deptChartData: data, deptNames: departments };
  }, [departmentSpending]);


  const requestCostChartData = useMemo(
    () =>
      departmentRequestCosts.map((entry) => ({
        ...entry,
        label: `${entry.month} • ${entry.department}`,
        approved_cost: Number(entry.approved_cost || 0),
        rejected_cost: Number(entry.rejected_cost || 0),
        total_cost: Number(entry.total_cost || 0),
      })),
    [departmentRequestCosts]
  );

  const requestCostTotals = useMemo(
    () =>
      departmentRequestCosts.reduce(
        (acc, entry) => ({
          approved: acc.approved + Number(entry.approved_cost || 0),
          rejected: acc.rejected + Number(entry.rejected_cost || 0),
          total: acc.total + Number(entry.total_cost || 0),
        }),
        { approved: 0, rejected: 0, total: 0 }
      ),
    [departmentRequestCosts]
  );

  const pendingTrend = useMemo(() => summary?.pending_vs_completed_trend || [], [summary]);
  const oldestPending = useMemo(() => summary?.oldest_pending_requests || [], [summary]);
  const completionRate = Number(summary?.completion_rate || 0);
  const avgPendingAge = Number(summary?.avg_pending_age_days || 0);
  const approvalTimeDays = Number(summary?.avg_approval_time_days || 0);
  const totalRequests = Number(summary?.total_requests || 0);
  const approvedRequests = Number(summary?.approved_requests || 0);
  const pendingRequests = Number(summary?.pending_requests || 0);
  const completedRequests = Number(summary?.completed_requests || 0);
  const rejectedRequests = Number(summary?.rejected_requests || 0);

  const { totalSpend, topMonth, topDepartment } = useMemo(() => {
    if (!summary) {
      return { totalSpend: 0, topMonth: null, topDepartment: null };
    }

    const spendingByMonth = summary.spending_by_month || [];
    const total = spendingByMonth.reduce(
      (sum, item) => sum + Number(item.total_cost || 0),
      0
    );
    const monthWithHighestSpend = spendingByMonth.reduce((best, current) => {
      if (!best) return current;
      const currentValue = Number(current.total_cost || 0);
      const bestValue = Number(best.total_cost || 0);
      return currentValue > bestValue ? current : best;
    }, null);

    const departmentTotals = departmentSpending.reduce((acc, entry) => {
      const cost = Number(entry.total_cost || 0);
      acc[entry.department] = (acc[entry.department] || 0) + cost;
      return acc;
    }, {});

    const [leadingDepartment, leadingSpend] =
      Object.entries(departmentTotals).sort((a, b) => b[1] - a[1])[0] || [];

    return {
      totalSpend: total,
      topMonth: monthWithHighestSpend,
      topDepartment: leadingDepartment
        ? { name: leadingDepartment, total: leadingSpend }
        : null,
    };
  }, [departmentSpending, summary]);

  const backlogChange = useMemo(() => {
    if (!pendingTrend || pendingTrend.length < 2) return null;
    const last = pendingTrend[pendingTrend.length - 1];
    const previous = pendingTrend[pendingTrend.length - 2];
    if (
      last?.pending_count === undefined ||
      previous?.pending_count === undefined
    )
      return null;
    const diff = Number(last.pending_count) - Number(previous.pending_count);
    const pct = previous.pending_count
      ? (diff / Number(previous.pending_count)) * 100
      : null;
    return { diff, pct };
  }, [pendingTrend]);

  const completionSpark = useMemo(
    () =>
      pendingTrend.slice(-6).map((item) => ({
        month: item.month,
        completions: Number(item.completed_count || 0),
      })),
    [pendingTrend]
  );

  const backlogSpark = useMemo(
    () =>
      pendingTrend.slice(-6).map((item) => ({
        month: item.month,
        pending: Number(item.pending_count || 0),
      })),
    [pendingTrend]
  );

  const spendingSpark = useMemo(
    () => (summary?.spending_by_month || []).slice(-6),
    [summary]
  );


  const procurementBacklogStages = useMemo(
    () =>
      (summary?.procurement_backlog_by_stage || []).map((entry) => ({
        ...entry,
        request_count: Number(entry.request_count || 0),
        procured_cost: Number(entry.procured_cost || 0),
      })),
    [summary]
  );

  const pendingAgingBuckets = useMemo(
    () =>
      (summary?.pending_aging_buckets || []).map((entry) => ({
        ...entry,
        request_count: Number(entry.request_count || 0),
        procured_cost: Number(entry.procured_cost || 0),
      })),
    [summary]
  );

  const demandVsProcuredChartData = useMemo(
    () =>
      (summary?.department_demand_vs_procured || [])
        .slice(-12)
        .map((entry) => ({
          ...entry,
          label: `${entry.month} • ${entry.department}`,
          submitted_cost: Number(entry.submitted_cost || 0),
          approved_cost: Number(entry.approved_cost || 0),
          actual_procured_cost: Number(entry.actual_procured_cost || 0),
        })),
    [summary]
  );

  const dataQualityAlerts = summary?.data_quality_alerts || [];
  const procurementValueCompletionRate = Number(
    summary?.procurement_value_completion_rate || 0
  );

  const operationalDashboards = useMemo(() => {
    const personal = [
      {
        label: translate('myPendingApprovals', {
          defaultValue: 'My pending approvals',
        }),
        value: formatAmount(pendingRequests),
      },
      {
        label: translate('myAssignedRequests', {
          defaultValue: 'My assigned requests',
        }),
        value: formatAmount(totalRequests),
      },
      {
        label: translate('urgentActions', { defaultValue: 'Urgent actions' }),
        value: formatAmount(oldestPending.length),
      },
      {
        label: translate('slaBreaches', { defaultValue: 'SLA breaches' }),
        value: formatAmount(oldestPending.filter((item) => Number(item.age_days || 0) > 14).length),
      },
    ];

    const procurement = [
      { label: translate('prAging', { defaultValue: 'PR aging' }), value: `${avgPendingAge.toFixed(1)}d` },
      { label: translate('rfqCycleTime', { defaultValue: 'RFQ cycle time' }), value: `${approvalTimeDays.toFixed(1)}d` },
      { label: translate('poDelays', { defaultValue: 'PO delays' }), value: formatAmount(Math.max(0, pendingRequests - approvedRequests)) },
      { label: translate('emergencyPurchases', { defaultValue: 'Emergency purchases' }), value: formatAmount(rejectedRequests) },
      { label: translate('supplierDelays', { defaultValue: 'Supplier delays' }), value: formatAmount(oldestPending.filter((item) => Number(item.age_days || 0) > 30).length) },
    ];

    const executive = [
      { label: translate('budgetUtilization', { defaultValue: 'Budget utilization' }), value: `${completionRate.toFixed(1)}%` },
      { label: translate('supplierPerformance', { defaultValue: 'Supplier performance' }), value: `${Math.max(0, 100 - avgPendingAge).toFixed(1)}%` },
      { label: translate('contractUtilization', { defaultValue: 'Contract utilization' }), value: `${Math.min(100, (approvedRequests / Math.max(totalRequests, 1)) * 100).toFixed(1)}%` },
      { label: translate('riskAlerts', { defaultValue: 'Risk alerts' }), value: formatAmount(oldestPending.filter((item) => Number(item.age_days || 0) > 21).length) },
      { label: translate('stockoutRisks', { defaultValue: 'Stockout risks' }), value: formatAmount(Math.max(0, pendingRequests - completedRequests)) },
    ];

    return { personal, procurement, executive };
  }, [
    translate,
    pendingRequests,
    totalRequests,
    oldestPending,
    avgPendingAge,
    approvalTimeDays,
    approvedRequests,
    rejectedRequests,
    completionRate,
    completedRequests,
  ]);

  if (error) return <p className="p-6 text-red-600">{error}</p>;
  if (!summary)
    return (
      <p className="p-6">
        {translate('loadingDashboard', { defaultValue: 'Loading dashboard...' })}
      </p>
    );

  return (
    <>
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6">
          <AnalyticsPageHeader
            eyebrow={translate('commandCenter', { defaultValue: 'Procurement command center' })}
            title={translate('overviewTitle', { defaultValue: '📊 Dashboard Overview' })}
            description={translate('overviewSubtitle', { defaultValue: 'Monitor demand, approvals, spend, and operational exceptions from one decision-ready view.' })}
            icon={LayoutDashboard}
            meta={[`${formatAmount(totalRequests)} total requests`, `${completionRate.toFixed(1)}% completion rate`, `Reporting year ${year}`]}
            actions={<button type="button" onClick={() => window.dispatchEvent(new Event(DASHBOARD_REFRESH_EVENT))} className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-blue-50"><RefreshCw className="h-4 w-4" />{" "}{uiText("Refresh data")}</button>}
          />
        </div>
        <p className="mb-4 text-sm text-slate-600">{translate('timingNote', { defaultValue: 'Timing metrics exclude recorded approval hold periods.' })}</p>
        <div className="mb-8"><CurrentProcurementPriorities status={priorityState.status} entries={priorityState.entries} /></div>

        {/* Overview Highlights */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <Card className="relative overflow-hidden bg-gradient-to-br from-purple-50 via-white to-blue-50 border border-purple-100 shadow-sm">
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-purple-100 blur-3xl" aria-hidden />
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm text-purple-700 font-medium">
                  {translate('completionPerformance', {
                    defaultValue: 'Completion performance',
                  })}
                </p>
                <p className="text-3xl font-bold text-purple-900">
                  {`${completionRate.toFixed(1)}%`}
                </p>
                <p className="text-xs text-purple-600">
                  {translate('completionHelper', {
                    defaultValue: 'Share of requests completed so far',
                  })}
                </p>
              </div>
              <span
                className={`px-2 py-1 text-xs rounded-full font-semibold shadow-sm ${
                  completionRate >= 90
                    ? 'bg-green-100 text-green-700'
                    : completionRate >= 70
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {completionRate >= 90
                  ? translate('statusOnTrack', { defaultValue: 'On track' })
                  : completionRate >= 70
                  ? translate('statusWatchlist', { defaultValue: 'Watchlist' })
                  : translate('statusActionNeeded', {
                      defaultValue: 'Action needed',
                    })}
              </span>
            </div>
            <div className="mt-4 flex items-center gap-4">
              <div className="relative w-20 h-20">
                <svg viewBox="0 0 36 36" className="w-full h-full">
                  <path
                    className="text-purple-100"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className="text-purple-600"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${Math.min(completionRate, 100)}, 100`}
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <text
                    x="18"
                    y="20.35"
                    className="text-xs font-semibold fill-purple-700"
                    textAnchor="middle"
                  >
                    {`${completionRate.toFixed(0)}%`}
                  </text>
                </svg>
              </div>
              <div className="flex-1">
                <div className="h-2 w-full bg-purple-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-500 to-blue-500"
                    style={{ width: `${Math.min(completionRate, 100)}%` }}
                  />
                </div>
                <p className="mt-3 text-xs text-purple-700 flex items-center justify-between">
                  <span>
                    {translate('approvalSpeed', {
                      defaultValue:
                        'Average approval time of {{days}} days keeps work moving',
                      days: approvalTimeDays.toFixed(1),
                    })}
                  </span>
                  {backlogChange?.pct !== null && backlogChange?.pct !== undefined && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-white border text-[10px] text-purple-700 shadow-sm">
                      {translate('changeSinceLastMonth', {
                        defaultValue: '{{pct}} vs last month',
                        pct: `${(backlogChange.pct || 0).toFixed(1)}%`,
                      })}
                    </span>
                  )}
                </p>
                <div className="mt-2 h-16">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={completionSpark}
                      margin={{ top: 5, right: 0, left: -10, bottom: 0 }}
                    >
                      <Tooltip cursor={false} />
                      <Line
                        type="monotone"
                        dataKey="completions"
                        stroke="#7c3aed"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </Card>

          <Card className="border border-blue-50 bg-gradient-to-br from-blue-50 via-white to-indigo-50 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-blue-700 font-medium mb-2">
                  {translate('spendHighlights', { defaultValue: 'Spending insights' })}
                </p>
                <p className="text-2xl font-bold text-blue-800">
                  {translate('yearToDateSpend', {
                    defaultValue: 'YTD {{amount}}',
                    amount: formatAmount(totalSpend),
                  })}
                </p>
                <p className="text-xs text-blue-600">
                  {translate('viewingYear', { defaultValue: 'Viewing {{year}}', year })}
                </p>
              </div>
              {topMonth && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-white border border-blue-100 text-blue-700 shadow-sm">
                  <span>🏆</span>
                  {translate('topSpendingMonth', {
                    defaultValue: 'Highest spend in {{month}}',
                    month: topMonth.month,
                  })}
                </span>
              )}
            </div>
            <div className="mt-3 h-16">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={spendingSpark}
                  margin={{ top: 0, right: 0, left: -15, bottom: 0 }}
                >
                  <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.05)' }} />
                  <Bar
                    dataKey="total_cost"
                    fill="#3b82f6"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-xs text-gray-500">
              {topMonth
                ? translate('topSpendingMonth', {
                    defaultValue: 'Highest spend in {{month}}',
                    month: topMonth.month,
                  })
                : translate('topSpendingMonthFallback', {
                    defaultValue: 'Monthly spending will appear as data arrives',
                  })}
            </p>
            {topDepartment && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-xs text-blue-800 font-semibold">
                  {translate('leadingDepartment', {
                    defaultValue: 'Leading department',
                  })}
                </p>
                <p className="text-sm text-blue-900">
                  {translate('departmentSpend', {
                    defaultValue: '{{name}} with {{amount}} spent',
                    name: topDepartment.name,
                    amount: formatAmount(topDepartment.total),
                  })}
                </p>
              </div>
            )}
          </Card>

          <Card className="border border-amber-50 bg-gradient-to-br from-amber-50 via-white to-orange-50 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-amber-700 font-medium mb-2">
                  {translate('backlogPulse', { defaultValue: 'Backlog pulse' })}
                </p>
                <p className="text-2xl font-bold text-amber-800">
                  {backlogChange
                    ? `${backlogChange.diff > 0 ? '+' : ''}${backlogChange.diff.toFixed(0)}`
                    : translate('backlogStable', { defaultValue: 'Stable' })}
                </p>
                <p className="text-xs text-amber-700 mb-3">
                  {backlogChange
                    ? backlogChange.diff > 0
                      ? translate('backlogGrowing', {
                          defaultValue: 'Pending queue grew vs last month',
                        })
                      : translate('backlogImproving', {
                          defaultValue: 'Pending queue is shrinking',
                        })
                    : translate('backlogNoChange', {
                        defaultValue:
                          'Tracking changes once two months are available',
                      })}
                </p>
              </div>
              {backlogChange?.pct !== null && backlogChange?.pct !== undefined && (
                <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-white border border-amber-100 text-amber-700 shadow-sm">
                  {translate('pendingChange', {
                    defaultValue: '{{pct}} change',
                    pct: `${(backlogChange.pct || 0).toFixed(1)}%`,
                  })}
                </span>
              )}
            </div>
            <div className="mt-3 h-16">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={backlogSpark}
                  margin={{ top: 0, right: 0, left: -10, bottom: 0 }}
                >
                  <Tooltip cursor={false} />
                  <Line
                    type="monotone"
                    dataKey="pending"
                    stroke="#d97706"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span>{translate('pendingNow', { defaultValue: 'Current pending' })}</span>
              <span className="font-semibold text-gray-800">
                {formatAmount(pendingRequests)}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
              <span>{translate('agingIndicator', { defaultValue: 'Avg pending age' })}</span>
              <span className="font-semibold text-gray-800">
                {translate('daysValue', {
                  defaultValue: '{{days}} days',
                  days: avgPendingAge.toFixed(1),
                })}
              </span>
            </div>
          </Card>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="text-center border border-slate-100 shadow-sm bg-white/70 backdrop-blur">
            <h3 className="text-sm text-gray-500">
              {translate('totalRequests', { defaultValue: 'Total Requests' })}
            </h3>
            <p className="text-xl font-bold text-blue-600">{formatAmount(totalRequests)}</p>
            <p className="text-xs text-gray-400">
              {translate('requestsLabel', { defaultValue: 'requests' })}
            </p>
          </Card>
          <Card className="text-center border border-green-100 shadow-sm bg-green-50">
            <h3 className="text-sm text-gray-600">
              {translate('approved', { defaultValue: 'Approved' })}
            </h3>
            <p className="text-xl font-bold text-green-700">{formatAmount(approvedRequests)}</p>
            <p className="text-xs text-green-600">
              {translate('completionHelper', {
                defaultValue: 'Share of requests completed so far',
              })}
            </p>
          </Card>
          <Card className="text-center border border-amber-100 shadow-sm bg-amber-50">
            <h3 className="text-sm text-gray-600">
              {translate('pending', { defaultValue: 'Pending' })}
            </h3>
            <p className="text-xl font-bold text-amber-700">{formatAmount(pendingRequests)}</p>
            <p className="text-xs text-amber-700">
              {translate('agingIndicator', { defaultValue: 'Avg pending age' })}
            </p>
          </Card>
          <Card className="text-center border border-blue-100 shadow-sm bg-blue-50">
            <h3 className="text-sm text-gray-600">
              {translate('completed', { defaultValue: 'Completed' })}
            </h3>
            <p className="text-xl font-bold text-blue-700">{formatAmount(completedRequests)}</p>
            <p className="text-xs text-blue-600">
              {translate('trendLabel', { defaultValue: 'Last 6 months trend' })}
            </p>
          </Card>
          <Card className="text-center border border-rose-100 shadow-sm bg-rose-50">
            <h3 className="text-sm text-gray-600">
              {translate('rejected', { defaultValue: 'Rejected' })}
            </h3>
            <p className="text-xl font-bold text-rose-700">{formatAmount(rejectedRequests)}</p>
            <p className="text-xs text-rose-600">
              {translate('changeSinceLastMonth', { defaultValue: 'vs last month' })}
            </p>
          </Card>
          <Card className="text-center border border-indigo-100 shadow-sm bg-indigo-50">
            <h3 className="text-sm text-gray-600">
              {translate('completionRate', { defaultValue: 'Completion Rate' })}
            </h3>
            <p className="text-xl font-bold text-indigo-700">{`${completionRate.toFixed(1)}%`}</p>
            <p className="text-xs text-indigo-600">
              {translate('completionSparkLabel', { defaultValue: 'Completion velocity' })}
            </p>
          </Card>
          <Card className="text-center border border-sky-100 shadow-sm bg-sky-50">
            <h3 className="text-sm text-gray-600">
              {translate('avgApprovalTime', { defaultValue: 'Avg Approval Time (days)' })}
            </h3>
            <p className="text-xl font-bold text-sky-700">{approvalTimeDays.toFixed(2)}</p>
            <p className="text-xs text-sky-600">
              {translate('approvalSpeed', {
                defaultValue:
                  'Average approval time of {{days}} days keeps work moving',
                days: approvalTimeDays.toFixed(1),
              })}
            </p>
          </Card>
          <Card className="text-center border border-amber-100 shadow-sm bg-amber-50/70">
            <h3 className="text-sm text-gray-600">
              {translate('avgPendingAge', { defaultValue: 'Avg Pending Age (days)' })}
            </h3>
            <p className="text-xl font-bold text-amber-700">{avgPendingAge.toFixed(1)}</p>
            <p className="text-xs text-amber-600">
              {translate('pendingChange', {
                defaultValue: '{{pct}} change',
                pct:
                  backlogChange?.pct !== null && backlogChange?.pct !== undefined
                    ? `${(backlogChange.pct || 0).toFixed(1)}%`
                    : '--',
              })}
            </p>
          </Card>
        </div>

        {/* Charts */}
        <div className="mb-10">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            {translate('operationalDashboards', { defaultValue: 'Operational Dashboards' })}
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[
              {
                title: translate('personalDashboard', { defaultValue: 'Personal Dashboard' }),
                subtitle: translate('personalDashboardSubtitle', {
                  defaultValue: 'Pending actions and SLA commitments for each user',
                }),
                items: operationalDashboards.personal,
              },
              {
                title: translate('procurementDashboard', { defaultValue: 'Procurement Dashboard' }),
                subtitle: translate('procurementDashboardSubtitle', {
                  defaultValue: 'Execution bottlenecks, delays, and procurement exceptions',
                }),
                items: operationalDashboards.procurement,
              },
              {
                title: translate('executiveDashboard', { defaultValue: 'Executive Dashboard' }),
                subtitle: translate('executiveDashboardSubtitle', {
                  defaultValue: 'Utilization, supplier risk, and enterprise alerts',
                }),
                items: operationalDashboards.executive,
              },
            ].map((panel) => (
              <Card key={panel.title} className="border border-slate-200 shadow-sm">
                <h3 className="text-base font-semibold text-slate-900">{panel.title}</h3>
                <p className="text-xs text-slate-500 mb-3">{panel.subtitle}</p>
                <div className="space-y-2">
                  {panel.items.map((metric) => (
                    <div
                      key={metric.label}
                      className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2"
                    >
                      <span className="text-sm text-slate-700">{metric.label}</span>
                      <span className="text-sm font-semibold text-slate-900">{metric.value}</span>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>


        <div className="mb-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                {translate('procurementControlTower', {
                  defaultValue: 'Procurement Control Tower',
                })}
              </h2>
              <p className="text-sm text-gray-500">
                {translate('procurementControlTowerSubtitle', {
                  defaultValue:
                    'Actionable backlog, aging, demand-to-procurement, and data quality analytics.',
                })}
              </p>
            </div>
            <span className="inline-flex w-fit rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
              {translate('valueCompletionRate', {
                defaultValue: 'Value completion: {{rate}}%',
                rate: procurementValueCompletionRate.toFixed(1),
              })}
            </span>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <Card className="border border-amber-100 shadow-sm">
              <h3 className="text-base font-semibold text-slate-900 mb-1">
                {translate('backlogByStage', { defaultValue: 'Procurement Backlog by Stage' })}
              </h3>
              <p className="text-xs text-slate-500 mb-3">
                {translate('backlogByStageSubtitle', {
                  defaultValue: 'Approved requests stay pending until every requested item is procured.',
                })}
              </p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={procurementBacklogStages} margin={{ top: 8, right: 16, bottom: 50, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="stage" angle={-25} textAnchor="end" interval={0} height={70} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar
                    dataKey="request_count"
                    fill="#f59e0b"
                    name={translate('requests', { defaultValue: 'Requests' })}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card className="border border-rose-100 shadow-sm">
              <h3 className="text-base font-semibold text-slate-900 mb-1">
                {translate('pendingAgingBuckets', { defaultValue: 'Pending Aging Buckets' })}
              </h3>
              <p className="text-xs text-slate-500 mb-3">
                {translate('pendingAgingBucketsSubtitle', {
                  defaultValue: 'Shows how much of the procurement queue is moving into riskier age bands.',
                })}
              </p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={pendingAgingBuckets} margin={{ top: 8, right: 16, bottom: 20, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="bucket" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar
                    dataKey="request_count"
                    fill="#f43f5e"
                    name={translate('requests', { defaultValue: 'Requests' })}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card className="xl:col-span-2 border border-blue-100 shadow-sm">
              <h3 className="text-base font-semibold text-slate-900 mb-1">
                {translate('demandVsProcured', { defaultValue: 'Department Demand vs Actual Procurement' })}
              </h3>
              <p className="text-xs text-slate-500 mb-3">
                {translate('demandVsProcuredSubtitle', {
                  defaultValue:
                    'Compares submitted demand, approved value, and what was actually procured for the latest months.',
                })}
              </p>
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={demandVsProcuredChartData} margin={{ top: 8, right: 16, bottom: 85, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" angle={-35} textAnchor="end" interval={0} height={95} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="submitted_cost"
                    fill="#94a3b8"
                    name={translate('submittedDemand', { defaultValue: 'Submitted demand' })}
                  />
                  <Bar
                    dataKey="approved_cost"
                    fill="#60a5fa"
                    name={translate('approvedValue', { defaultValue: 'Approved value' })}
                  />
                  <Bar
                    dataKey="actual_procured_cost"
                    fill="#10b981"
                    name={translate('actualProcured', { defaultValue: 'Actual procured' })}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card className="xl:col-span-2 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">
                    {translate('dataQualityAlerts', { defaultValue: 'Data Quality Alerts' })}
                  </h3>
                  <p className="text-xs text-slate-500">
                    {translate('dataQualityAlertsSubtitle', {
                      defaultValue: 'Records that can distort procurement KPIs or require cleanup.',
                    })}
                  </p>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                  {translate('alertCount', {
                    defaultValue: '{{count}} issue types',
                    count: dataQualityAlerts.length,
                  })}
                </span>
              </div>
              {dataQualityAlerts.length ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-slate-600">
                          {translate('issue', { defaultValue: 'Issue' })}
                        </th>
                        <th className="px-3 py-2 text-right font-semibold text-slate-600">
                          {translate('count', { defaultValue: 'Count' })}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {dataQualityAlerts.map((alert) => (
                        <tr key={alert.issue_key}>
                          <td className="px-3 py-2 text-slate-700">{alert.issue_label}</td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-900">
                            {formatAmount(alert.issue_count)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  {translate('noDataQualityAlerts', {
                    defaultValue: 'No data quality issues found for the current dashboard scope.',
                  })}
                </p>
              )}
            </Card>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h2 className="text-lg font-semibold mb-2">
              {translate('monthlySpending', { defaultValue: 'Actual Procured Spending' })}
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={summary?.spending_by_month || []}>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="total_cost" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold">
                {translate('departmentSpending', { defaultValue: 'Actual Procured Department Spending' })}
              </h2>
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value, 10))}
                className="border rounded p-1 text-sm"
              >
                {Array.from({ length: 5 }).map((_, i) => {
                  const y = new Date().getFullYear() - i;
                  return (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  );
                })}
              </select>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={deptChartData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                {deptNames.map((d, idx) => (
                  <Line key={d} type="monotone" dataKey={d} stroke={COLORS[idx % COLORS.length]} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="md:col-span-2">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2 gap-2">
              <div>
                <h2 className="text-lg font-semibold">
                  {translate('departmentRequestCosts', {
                    defaultValue: 'Department Request Cost by Decision',
                  })}
                </h2>
                <p className="text-sm text-gray-500">
                  {translate('departmentRequestCostsSubtitle', {
                    defaultValue:
                      'Monthly submitted request value split by approved and rejected requests.',
                  })}
                </p>
              </div>
              <div className="flex gap-2 text-xs text-slate-600">
                <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">
                  {translate('approvedCostTotal', {
                    defaultValue: 'Approved: {{amount}}',
                    amount: formatAmount(requestCostTotals.approved),
                  })}
                </span>
                <span className="rounded-full bg-rose-50 px-3 py-1 text-rose-700">
                  {translate('rejectedCostTotal', {
                    defaultValue: 'Rejected: {{amount}}',
                    amount: formatAmount(requestCostTotals.rejected),
                  })}
                </span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart
                data={requestCostChartData}
                margin={{ top: 8, right: 16, bottom: 80, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" angle={-35} textAnchor="end" interval={0} height={90} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="approved_cost"
                  stackId="requestCost"
                  fill="#10b981"
                  name={translate('approvedRequestCost', {
                    defaultValue: 'Approved request cost',
                  })}
                />
                <Bar
                  dataKey="rejected_cost"
                  stackId="requestCost"
                  fill="#f43f5e"
                  name={translate('rejectedRequestCost', {
                    defaultValue: 'Rejected request cost',
                  })}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="md:col-span-2">
            <h2 className="text-lg font-semibold mb-2">
              {translate('pendingVsCompleted', { defaultValue: 'Pending vs Completed Trend' })}
            </h2>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={pendingTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="pending_count"
                  stroke="#ffbb28"
                  name={translate('pending', { defaultValue: 'Pending' })}
                />
                <Line
                  type="monotone"
                  dataKey="completed_count"
                  stroke="#00C49F"
                  name={translate('completed', { defaultValue: 'Completed' })}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">
              {translate('topDepartments', { defaultValue: 'Top Requesting Departments' })}
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={summary.top_departments}
                  dataKey="request_count"
                  nameKey="name"
                  outerRadius={100}
                  label
                >
                  {summary.top_departments.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">
              {translate('rejectedRequests', { defaultValue: 'Rejected Requests' })}
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={summary.rejections_by_month}>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="rejected_count" stroke="#ff4d4f" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="md:col-span-2">
            <h2 className="text-lg font-semibold mb-2">
              {translate('oldestPending', { defaultValue: 'Oldest Pending Requests' })}
            </h2>
            <div className="bg-white shadow rounded p-4">
              {oldestPending.length ? (
                <ul className="space-y-3">
                  {oldestPending.map((request) => (
                    <li key={request.id} className="border-b last:border-b-0 pb-3 last:pb-0">
                      <p className="text-sm font-semibold text-gray-700">
                        {translate('requestWithDepartment', {
                          defaultValue: 'Request #{{id}} • {{department}}',
                          id: request.id,
                          department: request.department,
                        })}
                      </p>
                      <p className="text-sm text-gray-500 truncate">{request.justification}</p>
                      <p className="text-xs text-gray-400">
                        {translate('waitingDays', {
                          defaultValue: 'Waiting {{days}} days',
                          days: request.age_days.toFixed(1),
                        })}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">
                  {translate('noPendingBacklog', { defaultValue: 'No pending backlog 🎉' })}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
